import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const brandMilestones = [
  { year: '2015', title: '品牌创立', description: 'kaiyun.com 正式上线运营，开启体育娱乐新征程' },
  { year: '2017', title: '用户突破百万', description: '凭借优质服务积累百万忠实用户，成为行业新锐力量' },
  { year: '2019', title: '国际化布局', description: '业务拓展至东南亚、欧洲等多个市场，覆盖50+国家' },
  { year: '2021', title: '技术创新', description: '引入AI智能赔率系统，成为行业技术标杆' },
  { year: '2023', title: '用户突破5000万', description: '全球注册用户突破5000万，稳居行业领先地位' },
  { year: '2025', title: '荣获最佳平台奖', description: '获国际博彩博览会年度最佳平台大奖' },
];

const reputationItems = [
  {
    icon: 'ri-shield-check-line',
    title: '合法持牌运营',
    description: '持有国际公认的合法博彩运营牌照，接受权威机构监管，确保所有业务合规合法。',
  },
  {
    icon: 'ri-lock-line',
    title: '银行级数据加密',
    description: '采用SSL 256位加密技术，所有用户数据传输均经过严格加密保护，杜绝信息泄露。',
  },
  {
    icon: 'ri-bank-line',
    title: '资金第三方托管',
    description: '用户资金由持牌第三方金融机构托管，平台无权挪用，确保每一分钱安全。',
  },
  {
    icon: 'ri-verified-badge-line',
    title: '公平公正保障',
    description: '所有赔率和结果由独立第三方审计机构定期核查，确保平台运营的公平性。',
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20modern%20office%20headquarters%20building%20exterior%20with%20warm%20golden%20sunset%20lighting%2C%20professional%20corporate%20photography%2C%20clean%20architectural%20lines%2C%20premium%20atmosphere%2C%20soft%20natural%20light%20with%20warm%20tones&width=1800&height=600&seq=about-hero-bg&orientation=landscape"
            alt="关于开云体育"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3 tracking-tight">
                关于开云体育
              </h1>
              <p className="text-sm md:text-base text-background-200/90 max-w-lg mx-auto">
                值得信赖的全球体育娱乐平台
              </p>
            </div>
          </div>
        </section>

        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="w-full bg-background-50 py-14 md:py-20" id="brand">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight mb-3">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                kaiyun.com 开云体育是全球领先的综合性体育娱乐平台，自2015年成立以来，
                始终秉持"用户至上、安全第一"的核心价值观，致力于为全球体育爱好者提供优质的在线体育竞猜和娱乐服务。
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8 md:space-y-0">
                {brandMilestones.map((m, idx) => (
                  <div
                    key={m.year}
                    className={`relative flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} ${idx % 2 === 0 ? 'pl-10 md:pl-0' : 'pl-10 md:pl-0'}`}>
                      <div className="bg-background-50 border border-background-200 rounded-xl p-5 hover:border-primary-200 transition-all duration-300">
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-700 mb-2">
                          {m.year}
                        </span>
                        <h3 className="text-base font-bold text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-500">{m.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-8 h-8 rounded-full bg-primary-500 shrink-0 items-center justify-center relative z-10">
                      <span className="w-3 h-3 rounded-full bg-background-50"></span>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20" id="reputation">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight mb-3">
                信誉保障
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                我们深知信任是平台的基石，多重保障措施确保您的每一分权益
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {reputationItems.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl border border-background-200 p-5 md:p-6 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20" id="contact">
          <div className="w-full max-w-[960px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                如果您有任何问题或建议，欢迎通过以下方式与我们联系
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6 text-center hover:border-primary-200 transition-all duration-300">
                <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-500">support@kaiyun.com</p>
                <p className="text-xs text-foreground-400 mt-1">商务合作：biz@kaiyun.com</p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6 text-center hover:border-primary-200 transition-all duration-300">
                <div className="w-14 h-14 rounded-full bg-accent-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">在线客服</h3>
                <p className="text-sm text-foreground-500">7×24小时在线支持</p>
                <p className="text-xs text-foreground-400 mt-1">响应时间不超过30秒</p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6 text-center hover:border-primary-200 transition-all duration-300">
                <div className="w-14 h-14 rounded-full bg-secondary-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-share-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">社交媒体</h3>
                <div className="flex items-center justify-center gap-3 mt-2">
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                    <i className="ri-weibo-line text-sm text-foreground-600"></i>
                  </a>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                    <i className="ri-wechat-line text-sm text-foreground-600"></i>
                  </a>
                  <a href="#" className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                    <i className="ri-qq-line text-sm text-foreground-600"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `{"@context":"https://schema.org","@type":"AboutPage","name":"关于开云体育","description":"kaiyun.com 开云体育是全球领先的综合性体育娱乐平台","url":"https://www.kaiyun.com/about","isPartOf":{"@type":"WebSite","name":"kaiyun.com","url":"https://www.kaiyun.com/"}}` }} />
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}